/* Gloramous — the motion library's moving parts.

   Only the effects that cannot be expressed in CSS alone live here, and each
   is one small function exposed on window.GloramousMotion so the motion sheet
   can replay it and so any of them can be deleted without touching the others.

   WHAT IS DELIBERATELY NOT HERE: no scroll listener, no IntersectionObserver
   for entrances, no animation loop per effect. Entrances are `view()` timelines
   in the stylesheet; the pointer effects write a custom property and let a CSS
   transition ease it. The only rAF in the file is the count-up, which has to
   interpolate a number, and the velocity publisher, which shelf.js already
   runs a loop for.

   Everything checks reduced motion once, at the top, and does nothing if it is
   set -- the CSS already provides the finished resting state.
*/
(function () {
  'use strict';

  var OK = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
  var FINE = window.matchMedia('(pointer: fine)').matches;
  var M = {};

  /* ── 1. split a heading into words ────────────────────────────────────────
     Two spans per word: the outer clips, the inner moves. The word itself is
     still text, so nothing changes for a screen reader or for select-and-copy.

     Whitespace is preserved as real spaces between the outer spans, because
     `inline-block` collapses its own leading and trailing whitespace and the
     words would otherwise run together. */
  M.words = function (el) {
    if (!el || el.getAttribute('data-m-split') === 'done') return;
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;
    el.setAttribute('data-m-split', 'done');
    el.setAttribute('data-m-text', text);
    var parts = text.split(' ');
    el.textContent = '';
    parts.forEach(function (word, i) {
      var outer = document.createElement('span');
      outer.className = 'm-w';
      outer.style.setProperty('--i', i);
      var inner = document.createElement('span');
      inner.textContent = word;
      outer.appendChild(inner);
      el.appendChild(outer);
      if (i < parts.length - 1) el.appendChild(document.createTextNode(' '));
    });
    el.classList.add('m-words');
  };

  /* restart every word from the beginning -- used by the motion sheet, and the
     only reliable way to replay a CSS animation is to take the element out of
     the animating state, force a reflow, and put it back */
  M.replayWords = function (el) {
    if (!el) return;
    el.classList.add('is-idle');
    void el.offsetHeight;
    el.classList.remove('is-idle');
  };

  /* ── 2. magnetic ──────────────────────────────────────────────────────────
     Writes two numbers in the range -1..1. The CSS multiplies them by a pixel
     cap and eases with a transition, so this function never animates anything
     and there is nothing to throttle. */
  M.magnet = function (el) {
    if (!el || !OK || !FINE) return;

    /* WRAP BARE TEXT FIRST. `.m-magnet::before` is a ground layer at z-index 0
       and `.m-magnet > *` lifts every ELEMENT child to z-index 1 above it -- so
       a label that is a bare text node has no z-index and the ground paints
       over it. The hero button rendered as a solid black rectangle with its
       words underneath, and a contrast audit passed it, because the colours
       were right and nothing in a contrast check can see a pseudo-element on
       top.

       It looked fine on the motion sheet only because those demo buttons were
       written with a <span> around the label -- markup the real theme does not
       have. The effect had been verified against markup that flattered it. */
    var bare = [];
    for (var n = el.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 3 && n.nodeValue.trim()) bare.push(n);
    }
    if (bare.length) {
      var wrap = document.createElement('span');
      el.insertBefore(wrap, bare[0]);
      bare.forEach(function (t) { wrap.appendChild(t); });
    }

    el.classList.add('m-magnet');
    el.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      el.style.setProperty('--m-mx', (((e.clientX - r.left) / r.width) * 2 - 1).toFixed(3));
      el.style.setProperty('--m-my', (((e.clientY - r.top) / r.height) * 2 - 1).toFixed(3));
    });
    el.addEventListener('pointerleave', function () {
      el.style.setProperty('--m-mx', '0');
      el.style.setProperty('--m-my', '0');
    });
  };

  /* ── 3. odometer ──────────────────────────────────────────────────────────
     Builds a column of 0-9 per digit and moves each by transform. A digit that
     does not change does not move, which is the whole reason to do it this way
     rather than re-rendering the number.

     ACCESSIBILITY, corrected. An earlier version of this comment claimed the
     digits "ARE the text, in order, so a screen reader reads 1 2 3 correctly
     from the columns". That is false, and measuring it proved so: each column
     holds 0-9, so the element read out as twenty digits -- "0123456789
     0123456789" -- instead of "62". The columns are now aria-hidden and the
     real value sits beside them in a visually-hidden span. */
  M.odometer = function (el, value) {
    if (!el) return;
    var str = String(value);
    if (!OK) { el.textContent = str; return; }
    var digits = str.split('');
    if (el.getAttribute('data-m-odo') !== String(digits.length)) {
      el.textContent = '';
      el.className = (el.className + ' m-odo').trim();
      el.setAttribute('data-m-odo', String(digits.length));
      // the real value, for anything that is not looking at the columns
      var sr = document.createElement('span');
      sr.className = 'sr';
      sr.setAttribute('data-m-odo-value', '');
      el.appendChild(sr);
      digits.forEach(function () {
        var col = document.createElement('span');
        col.className = 'm-odo-d';
        col.setAttribute('aria-hidden', 'true');
        var strip = document.createElement('span');
        for (var n = 0; n <= 9; n++) {
          var cell = document.createElement('i');
          cell.textContent = String(n);
          strip.appendChild(cell);
        }
        col.appendChild(strip);
        el.appendChild(col);
      });
    }
    var srOut = el.querySelector('[data-m-odo-value]');
    if (srOut) srOut.textContent = str;
    var cols = el.querySelectorAll('.m-odo-d > span');
    digits.forEach(function (d, i) {
      if (cols[i]) cols[i].style.setProperty('--d', d);
    });
  };

  /* ── 4. count up ──────────────────────────────────────────────────────────
     The one effect that genuinely needs a loop: a number has to be
     interpolated. Eased out, so it decelerates into the real figure instead of
     stopping dead on it.

     It writes the FINAL value first and then animates, so if rAF never runs --
     a hidden tab, a throttled background -- the correct number is on screen
     rather than a zero. */
  M.countUp = function (el, to, ms) {
    if (!el) return;
    var target = Number(to);
    el.textContent = String(target);
    if (!OK) return;
    /* IT CAN STALL MID-COUNT, and the old comment here was wrong about that.
       It claimed "if rAF never runs the correct number is on screen" -- true
       only if rAF never runs AT ALL. If it runs once and then stops, which is
       exactly what a hidden or throttled document does, the number is left at
       whatever early frame it reached and stays there.

       Caught in a screenshot: the hero read "6 formulations / 105 first batch /
       0 laboratory" where the real figures are 121 / 2005 / 1. Three wrong
       numbers, on the homepage, stated as fact -- and no contrast or clipping
       check can see a wrong number.

       So: do not start while hidden, and snap to the target the moment the
       document goes away. A count-up that cannot finish must not start. */
    if (document.hidden) return;
    var from = 0, t0 = null, dur = ms || 1100, done = false;
    function settle() {
      if (done) return;
      done = true;
      el.textContent = String(target);
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) settle();
    }, { once: true });
    function frame(t) {
      if (done) return;
      if (document.hidden) { settle(); return; }
      if (t0 === null) t0 = t;
      var p = Math.min(1, (t - t0) / dur);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = String(Math.round(from + (target - from) * eased));
      if (p < 1) requestAnimationFrame(frame);
      else settle();
    }
    requestAnimationFrame(frame);
  };

  /* ── 5. flight ────────────────────────────────────────────────────────────
     A clone of the product image travels to the cart. The endpoints are two
     measured rectangles, so this cannot be a CSS keyframe -- but the animation
     itself is handed to the Web Animations API and runs off the main thread.

     The arc matters: a straight line reads as a file transfer. The midpoint is
     lifted above both endpoints so the path reads as something thrown.

     Returns the Animation so a caller can await it; resolves immediately when
     motion is off or either endpoint is missing. */
  M.flight = function (fromEl, toEl) {
    if (!OK || !fromEl || !toEl || typeof fromEl.animate !== 'function') {
      return Promise.resolve();
    }
    var a = fromEl.getBoundingClientRect();
    var b = toEl.getBoundingClientRect();
    if (!a.width || !b.width) return Promise.resolve();

    var flier = fromEl.cloneNode(true);
    flier.className = 'm-flier';
    flier.style.left = a.left + 'px';
    flier.style.top = a.top + 'px';
    flier.style.width = a.width + 'px';
    flier.style.height = a.height + 'px';
    document.body.appendChild(flier);

    var dx = (b.left + b.width / 2) - (a.left + a.width / 2);
    var dy = (b.top + b.height / 2) - (a.top + a.height / 2);
    var lift = Math.min(160, Math.abs(dy) * 0.45 + 60);

    var anim = flier.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: 'translate(' + dx * 0.5 + 'px,' + (dy * 0.5 - lift) + 'px) scale(.62)',
        opacity: .9, offset: .55 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.12)', opacity: 0 }
    ], { duration: 720, easing: 'cubic-bezier(.4,.05,.6,1)', fill: 'forwards' });

    /* Belt and braces. `anim.finished` never settles in a document that is
       hidden or throttled -- measured: the clone was still in the DOM after the
       animation should have ended. A leaked position:fixed clone at z-index
       9999 sits over the whole page and swallows nothing but looks broken, so
       it is removed on a timer as well as on the promise, whichever comes
       first. */
    var gone = false;
    function cleanup() { if (gone) return; gone = true; flier.remove(); }
    setTimeout(cleanup, 1400);
    return anim.finished.then(cleanup, cleanup);
  };

  /* ── 6. index the children of a stagger container ─────────────────────────
     The CSS delays off `--i`, and the count is however many items there are --
     which a stylesheet cannot know. */
  M.index = function (container) {
    if (!container) return;
    var kids = container.children, n = kids.length;
    for (var i = 0; i < n; i++) kids[i].style.setProperty('--i', i);
  };

  /* ── 7. pointer position on a container, 0..1 ─────────────────────────────
     Shared by the shelf parallax and anything else that wants it. */
  M.track = function (el, prop) {
    if (!el || !OK || !FINE) return;
    var name = prop || '--m-px';
    el.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      if (!r.width) return;
      var x = (e.clientX - r.left) / r.width;
      el.style.setProperty(name, Math.max(0, Math.min(1, x)).toFixed(4));
    });
    el.addEventListener('pointerleave', function () {
      el.style.setProperty(name, '0.5');
    });
  };

  /* -- 8. split text into per-character spans -------------------------------
     Shared by the split-flap, the letter lift and the decode. Spaces become
     non-breaking so a flipped or lifted word does not come apart, and the
     original string is stashed so a replay can rebuild from it. */
  M.chars = function (el, cls) {
    if (!el) return;
    var text = el.getAttribute('data-m-text') || el.textContent;
    el.setAttribute('data-m-text', text);
    el.textContent = '';
    var n = 0;
    text.split('').forEach(function (ch) {
      var c = document.createElement('span');
      c.className = 'm-c';
      c.style.setProperty('--i', n++);
      c.textContent = ch === ' ' ? '\u00a0' : ch;
      el.appendChild(c);
    });
    if (cls) el.classList.add(cls);
    return el;
  };

  /* -- 9. the loupe ---------------------------------------------------------
     A lens over the shelf, magnifying whatever is under the pointer. Two
     numbers: where the lens sits, and where its background must be so the
     magnified image lines up with the real one underneath.

     The second is the arithmetic that makes it work. The lens shows the image
     at `zoom` scale, so for the point under the pointer to appear at the
     lens's centre the background must be offset by that point's position times
     the zoom, minus half the lens. Get it wrong and the lens shows the right
     picture in the wrong place, which reads as a bug rather than a loupe.

     The shelf's own drift is a transform on .shelf-run, so the lens has to
     subtract it or the magnified bottles sit beside the real ones. */
  /* Works for BOTH pointer types. The desktop lens follows a hovering mouse;
     on a phone the same function drives it from the drag, because shelf.js adds
     `.is-dragging` and the CSS reveals the lens on that class. One code path,
     two gestures -- the alternative was a second nearly-identical function that
     would drift out of step with this one. */
  M.loupe = function (host, zoom) {
    if (!host || !OK) return;
    var lens = host.querySelector('.m-loupe');
    if (!lens) return;
    var z = zoom || 2.1;
    host.classList.add('m-loupe-host');
    host.addEventListener('pointermove', function (e) {
      var r = host.getBoundingClientRect();
      if (!r.width) return;
      var x = e.clientX - r.left;
      var half = lens.offsetWidth / 2;
      lens.style.setProperty('--m-lx', x.toFixed(1));
      var run = host.querySelector('.shelf-run');
      var drift = 0;
      if (run) {
        var m = /matrix\(1, 0, 0, 1, (-?[\d.]+)/.exec(getComputedStyle(run).transform);
        if (m) drift = parseFloat(m[1]);
      }
      lens.style.setProperty('--m-lbg', (-((x - drift) * z - half)).toFixed(1));
    });
  };

  /* -- 10. ripple from where the pointer landed -----------------------------
     Not from the centre. The centre is the version that reads as a stock
     component, because a real impact happens where it happens. */
  M.ripple = function (el) {
    if (!el || !OK) return;
    el.classList.add('m-ripple');
    /* its own clipping layer: `overflow:hidden` on the control would clip the
       magnetic label, which travels outside the box. Measured before fixing. */
    if (!el.querySelector('.m-ink')) {
      var ink = document.createElement('span');
      ink.className = 'm-ink';
      ink.setAttribute('aria-hidden', 'true');
      el.appendChild(ink);
    }
    el.addEventListener('pointerdown', function (e) {
      var r = el.getBoundingClientRect();
      if (!r.width) return;
      /* Clamped to 0..100. A pointer can report just outside the box on an
         edge press, and an unclamped value starts the ink off the control --
         measured -64.6%, -1786.6% from an event dispatched outside it. Same
         clamp the hero lamp already has, for the same reason. */
      var rx = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
      var ry = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
      el.style.setProperty('--m-rx', rx.toFixed(1));
      el.style.setProperty('--m-ry', ry.toFixed(1));
      el.classList.remove('is-rippling');
      void el.offsetHeight;
      el.classList.add('is-rippling');
    });
  };

  /* -- 11. morph a button to a tick and back --------------------------------
     The tick is injected once and lives in the button, so the morph is a class
     toggle. It reverts on a timer: a control stuck in its success state has
     stopped being a control. */
  M.morph = function (el, ms) {
    if (!el) return;
    if (!el.querySelector('.m-morph-tick')) {
      /* Wrap the LABEL, not everything. `while (el.firstChild)` swallowed the
         ripple's `.m-ink` layer into the label span -- and the label scales
         down on is-done, so the ink would have shrunk with it and painted
         above the button instead of beneath. Anything the motion library owns
         is left where it is. */
      var label = document.createElement('span');
      var kids = Array.prototype.slice.call(el.childNodes);
      kids.forEach(function (n) {
        if (n.nodeType === 1 && n.classList &&
            (n.classList.contains('m-ink') || n.classList.contains('m-morph-tick'))) return;
        label.appendChild(n);
      });
      el.insertBefore(label, el.firstChild);
      var tick = document.createElement('span');
      tick.className = 'm-morph-tick';
      tick.setAttribute('aria-hidden', 'true');
      tick.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"' +
        ' stroke="currentColor" stroke-width="2.4" stroke-linecap="round"' +
        ' stroke-linejoin="round"><path d="M4 12.5l5 5L20 6.5"/></svg>';
      el.appendChild(tick);
      el.classList.add('m-morph');
    }
    if (!OK) return;
    el.classList.add('is-done');
    setTimeout(function () { el.classList.remove('is-done'); }, ms || 1500);
  };

  /* -- 12. decode -----------------------------------------------------------
     Letters settle out of noise, left to right. The loop runs once and stops;
     it does not idle.

     Digits scramble against digits and letters against letters, so a batch
     code never briefly reads as something it could not be. */
  M.decode = function (el, ms) {
    if (!el) return;
    var final = el.getAttribute('data-m-text') || el.textContent;
    el.setAttribute('data-m-text', final);
    if (!OK) { el.textContent = final; return; }
    M.chars(el, 'm-decode');
    var cells = el.querySelectorAll('.m-c');
    var D = '0123456789', A = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    var t0 = null, dur = ms || 900;
    function frame(t) {
      if (t0 === null) t0 = t;
      var p = Math.min(1, (t - t0) / dur);
      var settled = Math.floor(p * cells.length);
      for (var i = 0; i < cells.length; i++) {
        var ch = final[i];
        if (i < settled || ch === ' ' || ch === '-') {
          cells[i].textContent = ch === ' ' ? '\u00a0' : ch;
        } else {
          var pool = /[0-9]/.test(ch) ? D : A;
          cells[i].textContent = pool[Math.floor(Math.random() * pool.length)];
        }
      }
      if (p < 1) requestAnimationFrame(frame);
      else for (var j = 0; j < cells.length; j++) {
        cells[j].textContent = final[j] === ' ' ? '\u00a0' : final[j];
      }
    }
    requestAnimationFrame(frame);
  };

  /* -- 13. two planes tracking the pointer in opposite directions -----------
     One listener on the shared ancestor, two numbers, and the CSS sends the
     layers opposite ways. Listening on each layer would fire twice and fight. */
  M.planes = function (host) {
    if (!host || !OK || !FINE) return;
    host.addEventListener('pointermove', function (e) {
      var r = host.getBoundingClientRect();
      if (!r.width || !r.height) return;
      host.style.setProperty('--m-hx', (((e.clientX - r.left) / r.width) * 2 - 1).toFixed(3));
      host.style.setProperty('--m-hy', (((e.clientY - r.top) / r.height) * 2 - 1).toFixed(3));
    });
    host.addEventListener('pointerleave', function () {
      host.style.setProperty('--m-hx', '0');
      host.style.setProperty('--m-hy', '0');
    });
  };

  /* -- 14. the curtain ------------------------------------------------------
     Once per session, on the first page only. A curtain on every navigation is
     a toll booth, and sessionStorage is the right scope: a new visit is a new
     arrival, a second page is not.

     try/catch because sessionStorage throws outright in a private window, and
     an exception here would take the rest of the wiring with it. */
  M.curtain = function () {
    if (!OK) return;
    try {
      if (sessionStorage.getItem('m-curtain') === '1') return;
      sessionStorage.setItem('m-curtain', '1');
    } catch (e) { /* no storage: show it once and move on */ }
    var c = document.createElement('div');
    c.className = 'm-curtain';
    c.setAttribute('aria-hidden', 'true');
    document.body.appendChild(c);
    setTimeout(function () { c.remove(); }, 1000);
  };

  /* ── the velocity lean's consumers ──────────────────────────────────────
     shelf.js used to write --m-vel to `:root` once per frame. Custom
     properties INHERIT, so a write on the root element invalidates
     custom-property inheritance for every element in the document. Measured
     on the catalogue: 8.40ms per frame writing to the root, 1.21ms writing to
     the consumers. There are four consumers and sixty-two cards, and all
     sixty-two were paying for the write.

     The list is cached and only re-queried while it is empty, because
     `.m-inertia` is added by the wiring pass below and may not exist yet the
     first time a scroll arrives. */
  var velTargets = null;
  M.vel = function (v) {
    if (!velTargets || !velTargets.length) {
      velTargets = document.querySelectorAll('.m-inertia');
      if (!velTargets.length) return;
    }
    for (var i = 0; i < velTargets.length; i++) {
      velTargets[i].style.setProperty('--m-vel', v);
    }
  };

  window.GloramousMotion = M;
  window.GloramousMotion.enabled = OK;
  window.GloramousMotion.pointerFine = FINE;
})();

/* ── wiring ─────────────────────────────────────────────────────────────────
   Which real elements get which effect. One list, so adding or removing an
   animation from the whole site is one line here rather than an edit in each
   of thirty-four section files -- and so the sections carry no knowledge of
   the motion at all.

   Runs after DOMContentLoaded. Everything inside is a no-op when reduced
   motion is set, because each library function checks once and returns. */
(function () {
  'use strict';
  var M = window.GloramousMotion;
  if (!M) return;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* THE REAL ADD-TO-CART CONTROLS. The morph, the recoil and the flight were
     all wired to `[data-add],button[name="add"]`, and neither attribute exists
     in a single .liquid file in this theme -- so three effects never fired on
     any device. These are the controls that actually exist:
       [data-add-to-cart]  the product form's submit and the buy bar's button
       [data-quick-id]     the quick-add on a card

     An explicit attribute rather than an inferred selector, because the first
     guess matched nothing and the obvious second guess -- a bare
     button[type=submit] -- would have matched the header SEARCH button. */
  var ADD_SELECTOR = '[data-add-to-cart],[data-quick-id]';

  ready(function () {
    /* 1. the hero headline, a word at a time. Only the hero: a word stagger is
          an arrival, and a page cannot arrive nine times. */
    M.words(document.querySelector('.hero-panel h1'));

    /* 2. NO MAGNETIC BUTTONS. Removed in the calm round (2026-09-16): a button
          that tips and slides toward the pointer is motion the shopper did not
          ask for, and the site was reported as making people nauseous. */

    /* 3. the cards lift. The class carries the transition, so hover and
          focus-visible both work without JS. */
    document.querySelectorAll('.card,.fcard,.big,.fam,.rung-card')
      .forEach(function (c) { c.classList.add('m-lift'); });

    /* 4. the statement's figures count up, once, when the band is reached.
          This is the one place an IntersectionObserver is right: a count-up
          has a beginning and an end, so it wants a trigger, not a timeline. */
    var nums = document.querySelectorAll('.statement-nums b, .lab-facts b');
    if (nums.length && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var raw = e.target.textContent.trim();
          var n = parseInt(raw.replace(/[^0-9]/g, ''), 10);
          // keep any suffix the copy had -- "20+" must not become "20"
          var suffix = raw.replace(/[0-9]/g, '');
          if (!isNaN(n)) {
            var host = document.createElement('span');
            host.className = 'm-num';
            e.target.textContent = '';
            e.target.appendChild(host);
            if (suffix) e.target.appendChild(document.createTextNode(suffix));
            M.countUp(host, n);
          }
          io.unobserve(e.target);
        });
      }, { threshold: 0.6 });
      nums.forEach(function (n) { io.observe(n); });
    }

    /* 5. the filter count rolls. It is the number that changes most often on
          the site, and it changed by blinking. */
    var countEl = document.querySelector('[data-cat-count]');
    if (countEl) {
      var mo = new MutationObserver(function () {
        var b = countEl.querySelector('b');
        if (!b || b.getAttribute('data-m-odo')) return;
        M.odometer(b, b.textContent.trim());
      });
      mo.observe(countEl, { childList: true, subtree: true, characterData: true });
    }

    /* 6. the cart badge rolls and pops. Two effects on one element because two
          things happen: the value changes, and something arrived. */
    /* NO BADGE POP. Removed 2026-09-16: the count scaled up and down on every
       add and the user read it as a glitch ("Cart ( 3 )"). The number changes;
       it does not need to jump. */
    var badge = null;
    if (badge) {
      var last = badge.textContent.trim();
      new MutationObserver(function () {
        var now = badge.textContent.trim();
        if (now === last) return;
        last = now;
        badge.classList.remove('m-pop');
        void badge.offsetHeight;
        badge.classList.add('m-pop');
      }).observe(badge, { childList: true, characterData: true, subtree: true });
    }

    /* 7. adding to the cart throws the bottle at the cart. The image is
          whichever one the page has -- the product media on a product page, the
          card's own image for a quick-add. */
    /* `:not(.saved-link)`. header.liquid renders `class="cart-link saved-link"`
       -- the saved-items heart -- 25 lines BEFORE the real cart link, so a bare
       querySelector('.cart-link') returned the heart. On desktop the bottle
       flew to the wrong icon; on a phone that heart is display:none, so it flew
       to nothing at all. */
    var cartIcon = document.querySelector('.cart-link:not(.saved-link)');
    if (cartIcon) {
      document.addEventListener('click', function (e) {
        var t = e.target.closest ? e.target.closest(ADD_SELECTOR) : null;
        if (!t) return;
        var img = (t.closest('.card,.fcard,.big') || document)
                    .querySelector('.card-img img,.fcard-img img,.big-img img,.product-media img');
        if (img) M.flight(img, cartIcon);
      }, true);
    }

    /* 8. NO LADDER BARS. They were wired here and removed: only one of the
          three rungs has a public price, so the bar asserted a figure the page
          does not show, and the blocks carry no strength to use instead. The
          effect is still in motion.css and demonstrated in the motion sheet --
          it needs a number behind it before it goes back on the band. */

    /* 9. staggered entrances, indexed so the delay follows the real count. */
    ['.plate-row', '.sres-list', '.rb-steps', '.saved-grid', '.gloss-items']
      .forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (c) {
          c.classList.add(sel === '.plate-row' ? 'm-wave' : 'm-stagger');
          M.index(c);
        });
      });

    /* 10. the shelf takes the pointer as well as the scroll. Two continuous
           inputs on the one element that is about the whole range. */
    /* no pointer parallax on the strip -- calm round */

    /* 11. and a loupe over it. The lens needs the same background image the
           strip uses, which the section writes as --shelf-img on .shelf, so it
           is inherited rather than passed. */
    var shelf = document.querySelector('.shelf');
    if (shelf && !shelf.querySelector('.m-loupe')) {
      var lens = document.createElement('span');
      lens.className = 'm-loupe';
      lens.setAttribute('aria-hidden', 'true');
      shelf.appendChild(lens);
      M.loupe(shelf, 2.1);
    }

    /* 12. every control gives under the finger, and the primary ones ripple
           from where it landed. Press on everything: it is the cheapest signal
           that a thing is a control at all. */
    document.querySelectorAll('.btn,.chip,.quick,.stepper button,.savebtn')
      .forEach(function (b) { b.classList.add('m-press'); });
    document.querySelectorAll('.btn:not(.btn--ghost)')
      .forEach(function (b) { M.ripple(b); });

    /* 13. add to cart becomes a tick, and the cart takes the hit. Both hang
           off the same click as the flight, so the three read as one event. */
    document.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest(ADD_SELECTOR) : null;
      if (!t) return;
      M.morph(t);
      /* no recoil on the cart link any more -- see the badge note above */
    }, true);

    /* 14. the hero's photograph and panel move opposite ways with the pointer.
           Opposite is the whole thing: same-direction is the page sliding. */
    /* REMOVED in the calm round: the photograph and the panel drifted in
       opposite directions with the pointer. Two planes sliding against each
       other is exactly the vestibular trigger the brief-book warns about. */

    /* 15. the cards turn to face the pointer -- GATED, which it was not. Every
           other pointer effect checks for a fine pointer; this one attached
           pointermove and pointerleave to 62 cards on a phone, each handler
           calling getBoundingClientRect, for a rule inside
           @media (pointer: fine) that cannot match there. */
    /* REMOVED in the calm round: 3D card tilt. A grid of cards turning to
       face the pointer is a moving floor. */

    /* 15b. the velocity lean, applied to the GRID rather than to every card.
            `.m-inertia` was defined in the stylesheet, described in a comment
            as the thing that gives the page one sense of momentum, and applied
            to nothing at all. One element per grid leans, which looks the same
            and is 62 fewer promoted layers. */
    /* REMOVED in the calm round: the scroll-velocity lean. The "Start here"
       grid skewed, stretched and jumped up to 24px with the speed of the
       scroll -- the page bending under the thumb. It was the effect reported
       as nauseating, and nothing replaces it: product grids stay still. */

    /* 16. the batch code decodes. It is the most machine-like text on the site
           and the only thing here a scramble suits; on a product name it would
           be a costume. */
    var batch = document.querySelector('[data-batch-code]');
    if (batch) M.decode(batch, 900);

    /* 17. one split-flap heading per page, and only where a page names itself.
           Twice is a gimmick. */
    var flap = document.querySelector('[data-m-flap]');
    if (flap) M.chars(flap, 'm-flap');

    /* 18. the nav's letters lift in sequence on hover. */
    /* 18-19 REMOVED in the calm round: the nav letters bouncing on hover and
       the full-screen curtain wiping up on the first visit. The navigation is
       read, not played with, and a first visit should show the shop at once. */
  });
})();
